import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { EmailApiResponse } from '@dreamai/types';
import { useAuth0 } from '@auth0/auth0-react';
import { fetchEmailData } from '../components/chat/canvas/dsl-components/email-helpers/emailApiHelpers';
import { useDependencyUpdates } from './DependencyUpdateProvider';

interface EmailState {
  email: EmailApiResponse | null;
  loading: boolean;
  error: string | null;
  errorDetails?: {
    type: 'UNAUTHORIZED' | 'NOT_FOUND' | 'PERMISSION_DENIED' | 'INTERNAL_ERROR';
    message: string;
    sameCompany?: boolean;
  };
  lastUpdated: number;
}

interface EmailContextType {
  getEmail: (emailId: string) => EmailState;
  refreshEmail: (emailId: string) => Promise<void>;
  updateEmailOptimistically: (
    emailId: string,
    updates: Partial<EmailApiResponse>,
  ) => void;
  isLoading: (emailId: string) => boolean;
}

const EmailContext = createContext<EmailContextType | null>(null);

interface EmailProviderProps {
  children: React.ReactNode;
}

export const EmailProvider: React.FC<EmailProviderProps> = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();
  const { registerCallback } = useDependencyUpdates();
  const [emails, setEmails] = useState<Map<string, EmailState>>(new Map());
  const unregisterCallbacksRef = useRef<Map<string, () => void>>(new Map());

  const getDefaultState = (): EmailState => ({
    email: null,
    loading: true,
    error: null,
    lastUpdated: 0,
  });

  const fetchEmail = useCallback(
    async (emailId: string): Promise<EmailState> => {
      try {
        const response = await fetchEmailData(emailId, getAccessTokenSilently);

        if (response.success && response.email) {
          return {
            email: response.email,
            loading: false,
            error: null,
            errorDetails: undefined,
            lastUpdated: Date.now(),
          };
        } else {
          return {
            email: null,
            loading: false,
            error: response.error || 'Failed to load email',
            errorDetails: response.errorDetails,
            lastUpdated: Date.now(),
          };
        }
      } catch (error: any) {
        console.error('Error fetching email:', error);

        // Extract error details from axios error response
        const errorData = error.response?.data;
        if (errorData?.errorDetails) {
          return {
            email: null,
            loading: false,
            error: errorData.error || 'Failed to load email',
            errorDetails: errorData.errorDetails,
            lastUpdated: Date.now(),
          };
        }

        return {
          email: null,
          loading: false,
          error: 'Failed to load email',
          errorDetails: undefined,
          lastUpdated: Date.now(),
        };
      }
    },
    [getAccessTokenSilently],
  );

  const refreshEmail = useCallback(
    async (emailId: string) => {
      setEmails((prev) => {
        const current = prev.get(emailId) || getDefaultState();
        const updated = new Map(prev);
        updated.set(emailId, {
          ...current,
          loading: true,
          error: null,
        });
        return updated;
      });

      const newState = await fetchEmail(emailId);

      setEmails((prev) => {
        const updated = new Map(prev);
        updated.set(emailId, newState);
        return updated;
      });
    },
    [fetchEmail],
  );

  const updateEmailOptimistically = useCallback(
    (emailId: string, updates: Partial<EmailApiResponse>) => {
      setEmails((prev) => {
        const current = prev.get(emailId);
        if (!current || !current.email) return prev;

        const updated = new Map(prev);
        updated.set(emailId, {
          ...current,
          email: {
            ...current.email,
            ...updates,
          },
          lastUpdated: Date.now(),
        });
        return updated;
      });
    },
    [],
  );

  const registerForUpdates = useCallback(
    (emailId: string) => {
      console.log('Here', emailId);
      // Unregister any existing callback for this email
      const existingUnregister = unregisterCallbacksRef.current.get(emailId);
      if (existingUnregister) {
        existingUnregister();
      }

      // Register for dependency updates on this specific email record
      const unregister = registerCallback(
        'agent_storage_emails',
        emailId,
        (update) => {
          console.log(`Email ${emailId} was updated, refreshing...`);
          refreshEmail(emailId);
        },
      );

      unregisterCallbacksRef.current.set(emailId, unregister);
    },
    [registerCallback, refreshEmail],
  );

  const getEmail = useCallback(
    (emailId: string): EmailState => {
      const existing = emails.get(emailId);

      if (!existing) {
        // Initialize the email and start fetching
        const initialState = getDefaultState();
        setEmails((prev) => {
          const updated = new Map(prev);
          updated.set(emailId, initialState);
          return updated;
        });

        // Start fetching and register for updates
        refreshEmail(emailId);
        registerForUpdates(emailId);

        return initialState;
      }

      return existing;
    },
    [emails, refreshEmail, registerForUpdates],
  );

  const isLoading = useCallback(
    (emailId: string): boolean => {
      const state = emails.get(emailId);
      return state?.loading ?? true;
    },
    [emails],
  );

  // Cleanup callbacks on unmount
  useEffect(() => {
    return () => {
      unregisterCallbacksRef.current.forEach((unregister) => unregister());
      unregisterCallbacksRef.current.clear();
    };
  }, []);

  const contextValue: EmailContextType = {
    getEmail,
    refreshEmail,
    updateEmailOptimistically,
    isLoading,
  };

  return (
    <EmailContext.Provider value={contextValue}>
      {children}
    </EmailContext.Provider>
  );
};

export const useEmail = (): EmailContextType => {
  const context = useContext(EmailContext);
  if (!context) {
    throw new Error('useEmail must be used within an EmailProvider');
  }
  return context;
};
